// Pitch map component — renders a 2D soccer/football pitch with play visualisation.
//
// Uses the shared @frontend/pitch library (extracted during this PR to share
// canvas rendering between the reporting app and coding app). The reporting app
// uses on-demand rendering (continuous: false) since the pitch only needs to
// redraw when data changes, unlike the coding app which needs 60fps for live tracking.
//
// DESIGN DECISION: Canvas (not SVG). The initial implementation used SVG, but raw
// API coordinates (meters) rendered as SVG pixels produced incorrect results — a
// point at (50, 30) meters renders at 50px, 30px, making the pitch microscopic.
// Canvas with an explicit coordinate transform layer handles the world→pixel
// mapping cleanly. See libs/pitch/docs/guide.md for details.
import type {
  DrillRegion,
  HitTestArrow,
  HitTestPoint,
  PitchInput,
  PitchTransform
} from '@frontend/pitch'
import {
  computeDrillBounds,
  computePitchBounds,
  drawArrow,
  drawDrillRegion,
  drawPitch,
  drawPoint,
  hitTestElements,
  usePitchCanvas
} from '@frontend/pitch'
import { usePitchCanvasColors } from '@frontend/react'
import { cn } from '@frontend/ui'
import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  type RefObject
} from 'react'
import type { PitchArrow, PitchPoint } from '../lib/play-visualization'

interface PitchMapProps {
  /** Pitch payload — sport + coordinates. Drives which pitch markings get
   *  drawn (soccer / American Football / Canadian Football) via the
   *  `drawPitch` dispatcher in `@frontend/pitch`. */
  pitch: PitchInput
  /** Circle markers to render on the pitch. */
  points?: PitchPoint[]
  /** Directional arrows to render on the pitch. */
  arrows?: PitchArrow[]
  /** Called when a point or arrow is clicked. */
  onElementClick?: (id: string) => void
  /** Id of the currently selected element (highlighted). */
  selectedElementId?: string | null
  /** Ref to the underlying canvas element (for canvasToImage snapshots). */
  canvasElRef?: RefObject<HTMLCanvasElement | null>
  /** Additional CSS class names for the outer wrapper. */
  className?: string
  /** Optional drill geometry (P1..P4 corners, optional P5..P8 goalposts).
   *  When provided, the dashed amber overlay is drawn and — when
   *  `focusOnDrill` is true — the camera animates to fit the region. */
  drillRegion?: DrillRegion
  /** Whether to zoom the camera into `drillRegion`. Defaults to true when a
   *  region is supplied; set false to draw the overlay without zooming. */
  focusOnDrill?: boolean
  /* Whether to render the attack direction arrow */
  renderAttackDirection?: boolean
}

/**
 * Convert world-space visualization data to canvas-space hit-test targets.
 *
 * Hit testing operates in canvas pixel space (not world meters) so the click
 * tolerance is consistent regardless of pitch scale or container size. This
 * function pre-transforms all elements from world→canvas coordinates.
 */
function buildHitTargets(
  points: PitchPoint[],
  arrows: PitchArrow[],
  t: PitchTransform
): { htPoints: HitTestPoint[]; htArrows: HitTestArrow[] } {
  const htPoints: HitTestPoint[] = points.map((p) => {
    const { cx, cy } = t.worldToCanvas(p.x, p.y)
    return { id: p.id, cx, cy }
  })

  const htArrows: HitTestArrow[] = arrows.map((a) => {
    const from = t.worldToCanvas(a.x, a.y)
    const to = t.worldToCanvas(a.endX, a.endY)
    return {
      id: a.id,
      fromCx: from.cx,
      fromCy: from.cy,
      toCx: to.cx,
      toCy: to.cy
    }
  })

  return { htPoints, htArrows }
}

export function PitchMap({
  pitch,
  points = [],
  arrows = [],
  onElementClick,
  selectedElementId,
  canvasElRef,
  className,
  drillRegion,
  focusOnDrill = true,
  renderAttackDirection = true
}: PitchMapProps) {
  const { fill, line } = usePitchCanvasColors()
  const coordinates = pitch.coordinates

  // When an element is selected, dim everything else
  const hasSelection = selectedElementId != null
  const hoveredIdRef = useRef<string | null>(null)

  // Drill bounds feed `usePitchCanvas.focusBounds`, which drives the lerp
  // zoom animation already built into the shared hook. Null = zoom back to
  // full pitch.
  const focusBounds = useMemo(
    () =>
      drillRegion && focusOnDrill ? computeDrillBounds(drillRegion) : null,
    [drillRegion, focusOnDrill]
  )

  // Pitch aspect ratio is derived from the coordinate bounds so AMF/CFL
  // render long-and-narrow and soccer keeps its ~3:2 shape — instead of
  // stretching every sport into a single hardcoded box.
  const aspectStyle = useMemo(() => {
    const b = computePitchBounds(coordinates)
    const w = Math.abs(b.maxX - b.minX) || 1
    const h = Math.abs(b.maxY - b.minY) || 1
    return { aspectRatio: `${w} / ${h}` }
  }, [coordinates])

  const { canvasRef, containerRef, transform, redraw } = usePitchCanvas({
    coordinates,
    padding: 30,
    focusBounds,
    onDraw: (ctx, t, w, h) => {
      drawPitch(ctx, pitch, t, w, h, {
        fill,
        line
      })

      // Drill zone overlay sits between the base pitch and the action
      // markers so player points/arrows stay on top of the dashed region.
      if (drillRegion) drawDrillRegion(ctx, drillRegion, t)

      // "Attack" label with long arrow line
      if (renderAttackDirection) drawAttackLabel(ctx, w)

      const hoveredId = hoveredIdRef.current

      for (const arrow of arrows) {
        const from = t.worldToCanvas(arrow.x, arrow.y)
        const to = t.worldToCanvas(arrow.endX, arrow.endY)
        const isSelected = selectedElementId === arrow.id
        const isHovered = hoveredId === arrow.id
        const opacity = hasSelection && !isSelected ? 0.25 : 1
        drawArrow(ctx, from, to, {
          color: arrow.color,
          opacity,
          lineWidth: isSelected ? 3.5 : isHovered ? 3 : 2.5,
          headLength: 12
        })
      }

      for (const point of points) {
        const pos = t.worldToCanvas(point.x, point.y)
        const isSelected = selectedElementId === point.id
        const isHovered = hoveredId === point.id
        const opacity = hasSelection && !isSelected ? 0.25 : 1
        drawPoint(ctx, pos, {
          color: point.color,
          opacity,
          radius: isHovered ? 8 : 7,
          selected: isSelected
        })
      }
    }
  })

  // Expose canvas element to parent via ref
  useImperativeHandle<HTMLCanvasElement | null, HTMLCanvasElement | null>(
    canvasElRef,
    () => canvasRef.current,
    [canvasRef]
  )

  // Trigger redraw when data changes
  useEffect(() => {
    redraw()
  }, [points, arrows, selectedElementId, drillRegion, redraw, fill, line])

  /**
   * Resolve the canvas-pixel click position from a mouse event,
   * then hit-test against all visible elements.
   */
  const hitTest = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      if (!transform) return null
      const rect = canvasRef.current?.getBoundingClientRect()
      if (!rect) return null

      const cx = e.clientX - rect.left
      const cy = e.clientY - rect.top
      const { htPoints, htArrows } = buildHitTargets(points, arrows, transform)

      return hitTestElements(htPoints, htArrows, cx, cy)
    },
    [transform, points, arrows, canvasRef]
  )

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      if (!onElementClick) return
      const result = hitTest(e)
      if (result) {
        // Toggle: click same element deselects
        onElementClick(result.id === selectedElementId ? '' : result.id)
      } else if (selectedElementId) {
        // Click on empty space deselects
        onElementClick('')
      }
    },
    [hitTest, onElementClick, selectedElementId]
  )

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      const result = hitTest(e)
      const newHoveredId = result?.id ?? null

      if (canvasRef.current) {
        canvasRef.current.style.cursor = newHoveredId ? 'pointer' : 'default'
      }

      // Only redraw if the hovered element changed
      if (newHoveredId !== hoveredIdRef.current) {
        hoveredIdRef.current = newHoveredId
        redraw()
      }
    },
    [hitTest, redraw, canvasRef]
  )

  const handleMouseLeave = useCallback(() => {
    if (hoveredIdRef.current) {
      hoveredIdRef.current = null
      redraw()
    }
  }, [redraw])

  return (
    <div
      ref={containerRef}
      className={cn('w-full', className)}
      style={aspectStyle}
    >
      <canvas
        ref={canvasRef}
        className='h-full w-full'
        onClick={handleClick}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      />
    </div>
  )
}

/**
 * Draw "Attack ————→" label centered above the pitch, matching Angular style.
 */
function drawAttackLabel(ctx: CanvasRenderingContext2D, canvasWidth: number) {
  const y = 16
  const color = 'rgba(255, 255, 255, 0.6)'
  const fontSize = 13

  ctx.save()
  ctx.font = `${fontSize}px sans-serif`
  ctx.fillStyle = color
  ctx.textAlign = 'right'
  ctx.textBaseline = 'middle'

  const centerX = canvasWidth / 2
  const lineLength = 50
  const gap = 8

  // "Attack" text
  ctx.fillText('Attack', centerX - gap, y)

  // Long arrow line
  const lineStartX = centerX + gap
  const lineEndX = lineStartX + lineLength

  ctx.strokeStyle = color
  ctx.lineWidth = 1.5
  ctx.beginPath()
  ctx.moveTo(lineStartX, y)
  ctx.lineTo(lineEndX, y)
  ctx.stroke()

  // Arrowhead
  const headLen = 6
  ctx.beginPath()
  ctx.moveTo(lineEndX, y)
  ctx.lineTo(lineEndX - headLen, y - headLen / 2)
  ctx.lineTo(lineEndX - headLen, y + headLen / 2)
  ctx.closePath()
  ctx.fillStyle = color
  ctx.fill()

  ctx.restore()
}
